import { Timestamp } from "firebase/firestore";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  where,
  query,
  limit,
  setDoc,
  runTransaction,
} from "firebase/firestore";
import { db } from "../config/firebase"; // Assuming this is where your firebase config is exported

/**
 * Verifies an invitation is valid
 */
export async function verifyInvitation(invitationId: string, token: string) {
  try {
    // Get the invitation using modern syntax
    const invitationRef = doc(db, "invitations", invitationId);
    const invitationDoc = await getDoc(invitationRef);

    // Check if invitation exists
    if (!invitationDoc.exists()) {
      return {
        valid: false,
        error: "Invitation not found",
      };
    }

    const invitation = invitationDoc.data();
    // Check invitation token
    if (invitation.invitationToken !== token) {
      return {
        valid: false,
        error: "Invalid invitation token",
      };
    }

    // Check status
    if (invitation.status !== "pending") {
      return {
        valid: false,
        error:
          invitation.status === "accepted"
            ? "Invitation already accepted"
            : invitation.status === "expired"
            ? "Invitation has expired"
            : "Invitation is no longer valid",
        // Always include invitation data for any non-pending status
        invitation: {
          id: invitationDoc.id,
          firstName: invitation.firstName,
          lastName: invitation.lastName,
          email: invitation.email,
          role: invitation.role,
          teamRole: invitation.teamRole,
          orgId: invitation.orgId,
          teamId: invitation.teamId,
        },
      };
    }

    // Check expiration
    const now = new Date();
    if (invitation?.expiresAt?.toDate() < now) {
      // Auto-update expired status
      await updateDoc(invitationRef, {
        status: "expired",
        updatedAt: Timestamp.now(),
      });

      return {
        valid: false,
        error: "Invitation has expired",
        invitation: {
          id: invitationDoc.id,
          firstName: invitation.firstName,
          lastName: invitation.lastName,
          email: invitation.email,
          role: invitation.role,
          teamRole: invitation.teamRole,
          orgId: invitation.orgId,
          teamId: invitation.teamId,
        },
      };
    }

    // Get organization details
    const orgRef = doc(db, "organizations", invitation.orgId);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      return {
        valid: false,
        error: "The organization no longer exists",
      };
    }

    // Return invitation data for the registration form
    return {
      valid: true,
      invitation: {
        id: invitationDoc.id,
        firstName: invitation.firstName,
        lastName: invitation.lastName,
        email: invitation.email,
        role: invitation.role,
        teamRole: invitation.teamRole,
        orgId: invitation.orgId,
        orgName: orgDoc.data().name,
        planType: orgDoc.data().planType,
        teamId: invitation.teamId,
      },
    };
  } catch (error) {
    console.error("Error verifying invitation:", error);
    return {
      valid: false,
      error: "Failed to verify invitation",
    };
  }
}

/**
 * Gets organization details from subscription ID
 */
export async function getOrganizationBySubscription(subscriptionId: string) {
  try {
    // Use subscriptionId directly as the document ID
    const orgRef = doc(db, "organizations", subscriptionId);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      return {
        found: false,
        error: "Organization not found",
      };
    }

    const orgData = orgDoc.data();

    return {
      found: true,
      organization: {
        id: orgDoc.id,
        name: orgData.name,
        owner: orgData.owner,
        planType: orgData.planType,
        planId: orgData.planId,
      },
    };
  } catch (error) {
    console.error("Error getting organization by subscription:", error);
    return {
      found: false,
      error: "Failed to retrieve organization",
    };
  }
}
export async function getOrganizationBySubscriptionByPlan(planId: Number) {
  try {
    const orgQuery = query(
      collection(db, "organizations"),
      where("planId", "==", planId),
      limit(1)
    );
    const orgSnapshot = await getDocs(orgQuery);

    if (orgSnapshot.empty) {
      return {
        found: false,
        error: "Organization not found",
      };
    }

    const orgDoc = orgSnapshot.docs[0];
    const orgData = orgDoc.data();

    return {
      found: true,
      organization: {
        id: orgDoc.id,
        name: orgData.name,
        owner: orgData.owner,
        planType: orgData.planType,
      },
    };
  } catch (error) {
    console.error("Error getting organization by subscription:", error);
    return {
      found: false,
      error: "Failed to retrieve organization",
    };
  }
}

/**
 * Gets organization directly using subscriptionId as the document ID
 */
export async function getOrganizationBySubscriptionId(subscriptionId: string) {
  try {
    // Use subscriptionId directly as the document ID
    const orgRef = doc(db, "organizations", subscriptionId);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      return {
        found: false,
        error: "Organization not found",
      };
    }

    const orgData = orgDoc.data();

    return {
      found: true,
      organization: {
        id: orgDoc.id,
        name: orgData.name,
        owner: orgData.owner,
        planType: orgData.planType,
        planId: orgData.planId,
      },
    };
  } catch (error) {
    console.error("Error getting organization by subscription ID:", error);
    return {
      found: false,
      error: "Failed to retrieve organization",
    };
  }
}
